import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, Trash2, X, Image as ImageIcon, Download } from 'lucide-react';
import { 
  uploadProjectPhoto, 
  getProjectPhotos, 
  deleteProjectPhoto,
  getProjectPhotoUrl,
  type ProjectPhoto 
} from '../services/projectPhotoService';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import CameraCapture from './CameraCapture';
import { useImageViewer } from './ImageViewerProvider';

interface ProjectPhotosProps {
  projectId: string;
  readOnly?: boolean;
}

export default function ProjectPhotos({ projectId, readOnly = false }: ProjectPhotosProps) {
  const [photos, setPhotos] = useState<ProjectPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState<string[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<ProjectPhoto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const { open: openImageViewer } = useImageViewer();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (projectId) {
      loadPhotos();
    }
  }, [projectId]);

  const loadPhotos = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    try {
      const projectPhotos = await getProjectPhotos(projectId);
      setPhotos(projectPhotos);
    } catch (error) {
      console.error('Error loading project photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const tempId = `temp-${Date.now()}-${i}`;
        
        try {
          setUploadingPhotos(prev => [...prev, tempId]);
          
          const result = await uploadProjectPhoto({
            projectId,
            file
          });

          if (result.success && result.photo) {
            setPhotos(prev => [result.photo!, ...prev]);
            toast.success(`Photo "${file.name}" uploaded successfully`);
          } else {
            console.error('❌ Photo upload failed:', result.error);
            toast.error(`Failed to upload ${file.name}: ${result.error}`);
          }
        } catch (error) {
          console.error('❌ Error uploading photo file:', error);
          toast.error(`Failed to upload ${file.name}`);
        } finally {
          setUploadingPhotos(prev => prev.filter(id => id !== tempId));
        }
      }
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = (photo: ProjectPhoto) => {
    setPhotoToDelete(photo);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!photoToDelete) return;

    setDeleteLoading(true);
    try {
      const success = await deleteProjectPhoto(photoToDelete.id);
      if (success) {
        setPhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
        toast.success('Photo deleted successfully');
        setIsDeleteModalOpen(false);
        setPhotoToDelete(null);
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openPhotoViewer = (photo: ProjectPhoto) => {
    openImageViewer({
      images: photos.map((p) => ({ src: getProjectPhotoUrl(p.file_path), caption: p.file_name, download: p.file_name })),
      index: Math.max(0, photos.indexOf(photo)),
    });
  };

  const handleDownload = async (photo: ProjectPhoto, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const photoUrl = getProjectPhotoUrl(photo.file_path);
      
      // Fetch the image
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = photo.file_name || `project-photo-${photo.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      toast.success('Photo downloaded successfully');
    } catch (error) {
      console.error('Error downloading photo:', error);
      toast.error('Failed to download photo');
    }
  };

  const handleCameraCapture = () => {
    setShowCamera(true);
  };

  const handleCameraPhotos = async (newPhotos: string[]) => {
    setShowCamera(false);
    
    if (newPhotos.length === 0) return;

    console.log('📸 Processing project camera photos:', newPhotos.length);
    setUploading(true);

    try {
      // Upload each photo
      for (let i = 0; i < newPhotos.length; i++) {
        const blobUrl = newPhotos[i];
        const fileName = `project-photo-${Date.now()}-${i + 1}.jpg`;
        
        // Track this photo as uploading
        setUploadingPhotos(prev => [...prev, blobUrl]);
        
        try {
          // Convert blob URL to File
          const file = await blobUrlToFile(blobUrl, fileName);
          
          // Upload to Supabase
          const result = await uploadProjectPhoto({
            projectId,
            file
          });

          if (result.success && result.photo) {
            // Add to photos list
            setPhotos(prev => [result.photo!, ...prev]);
            toast.success('Photo uploaded successfully');
            console.log('✅ Project camera photo uploaded successfully:', result.photo.id);
          } else {
            console.error('❌ Project camera photo upload failed:', result.error);
            toast.error(`Failed to upload photo: ${result.error}`);
          }
        } catch (error) {
          console.error('❌ Error processing project camera photo:', error);
          toast.error('Failed to process photo');
        } finally {
          // Remove from uploading list
          setUploadingPhotos(prev => prev.filter(url => url !== blobUrl));
          // Clean up blob URL
          URL.revokeObjectURL(blobUrl);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  /**
   * Convert blob URL to File object for upload
   */
  const blobUrlToFile = async (blobUrl: string, fileName: string): Promise<File> => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type });
  };

  if (isLoading) {
    return (
      <div className="px-3 py-4 text-center text-sm text-gray-500">
        Loading photos...
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Photo Capture Buttons - iOS Style */}
        {!readOnly && (
          <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
            <button
              onClick={handleCameraCapture}
              disabled={uploading || showCamera}
              className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Camera className="h-5 w-5 mr-2" />
              <span className="text-[15px] font-medium">{showCamera ? 'Camera Open...' : 'Open Camera'}</span>
            </button>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                } else {
                  toast.error('File input not available');
                }
              }}
              disabled={uploading}
              className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-[12px] hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Upload className="h-5 w-5 mr-2" />
              <span className="text-[15px] font-medium">Upload Photos</span>
            </button>
          </div>
        )}

        {/* Photo Grid */}
        {photos.length === 0 ? (
          <div className="text-center py-8">
            <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No photos added yet</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((photo) => {
                const photoUrl = getProjectPhotoUrl(photo.file_path);
                const isUploading = uploadingPhotos.includes(photo.id);

                return (
                  <div
                    key={photo.id}
                    className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                  >
                    <img
                      src={photoUrl}
                      alt={photo.file_name}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openPhotoViewer(photo)}
                      onError={(e) => {
                        // Fallback if image fails to load
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    
                    {/* Overlay with action buttons */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={(e) => handleDownload(photo, e)}
                        className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        title="Download photo"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {!readOnly && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(photo);
                          }}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          title="Delete photo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Uploading indicator */}
                    {isUploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-white text-sm">Uploading...</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Hidden file input */}
        {!readOnly && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        )}
      </div>

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={showCamera}
        onClose={handleCameraClose}
        onPhotosCapture={handleCameraPhotos}
        title="Project Photos"
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPhotoToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Photo"
        message={`Are you sure you want to delete "${photoToDelete?.file_name}"? This action cannot be undone.`}
        loading={deleteLoading}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}
